import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getCreateEmspUserOcpiTokenDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/create-emsp-user-ocpi-token.form-default-values';
import { CreateEmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/create-emsp-user-ocpi-token.model';
import React, { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useEmspBoCreateOcpiTokenForEmspUserMutation } from '@app/core/service/emsp-bo-emsp-user.service';

import {
  CreateEmspBoEmspUserOcpiTokenContextProvider,
  useEmspBoEmspUserContext,
} from '../emsp-bo-emsp-user.context';
import { EmspBoEmspUserOcpiTokenCreateFormController } from './emsp-bo-emsp-user-ocpi-token-create-form/emsp-bo-emsp-user-ocpi-token-create-form.controller';

interface EmspBoEmspUserOcpiTokenCreateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const EmspBoEmspUserOcpiTokenCreateController = ({
  onCancel,
  onSuccess,
}: EmspBoEmspUserOcpiTokenCreateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [createOcpiTokenForEmspUser] =
    useEmspBoCreateOcpiTokenForEmspUserMutation();
  const emspBoEmspUser = useEmspBoEmspUserContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<CreateEmspUserOcpiTokenModel> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(
      t('$.emsp-bo-emsp-user-ocpi-token-create.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    CreateEmspUserOcpiTokenModel
  > = (fieldValues) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    createOcpiTokenForEmspUser(fieldValues)
      .unwrap()
      .then(() => {
        toast.success(t('$.emsp-bo-emsp-user-ocpi-token-create.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.emsp-bo-emsp-user-ocpi-token-create.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getCreateEmspUserOcpiTokenDefaultValues(emspBoEmspUser.id);

  return (
    <CreateEmspBoEmspUserOcpiTokenContextProvider data={data}>
      <EmspBoEmspUserOcpiTokenCreateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </CreateEmspBoEmspUserOcpiTokenContextProvider>
  );
};
